import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Close,
  Save,
  CheckCircle,
  AutoAwesome,
  CreditCard,
  Visibility,
  VisibilityOff,
  LinkOff,
  Sync,
  ErrorOutline,
  NewReleasesOutlined,
  MailOutline,
} from "@mui/icons-material";
import { InputAdornment, Skeleton, useMediaQuery } from "@mui/material";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/EnterpriseAuthContext";
import { getAiTier } from "../services/usageService";
import {
  getModelDisplayName,
  getPlanModelId,
  isPremiumTier,
  MODEL_IDS,
} from "../constants/aiModels";
import { supabase } from "../lib/supabase";
import edgeFunctionService from "../services/edgeFunctionService";
import stripeService, { PLAN_KEYS } from "../services/stripeService";
import { getPlanFeaturesForDisplay } from "../services/usageService";
import { WHATS_NEW_LINK_PROPS } from "./WhatsNewPage";
import { getMarketingPreference, setMarketingPreference } from "../services/emailPreferencesService";
import { isAutopilotEnabled } from "../services/autopilotModel";
import AutopilotSettingsSection from "./autopilot/AutopilotSettingsSection";

/**
 * Render a tab panel container.
 * @param {object} props - Tab panel props.
 * @param {React.ReactNode} props.children - Tab contents.
 * @param {number} props.value - Active tab index.
 * @param {number} props.index - Panel index.
 * @returns {JSX.Element} Tab panel element.
 */
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Pricing plan cards with real Stripe Checkout and Customer Portal integration.
 * Current plan is read from authState.currentMembership.plan_name.
 * All post-purchase management (upgrades, downgrades, seat changes, cancellations)
 * is handled through the Stripe Customer Portal.
 * @returns {JSX.Element} Pricing plans section.
 */
/** Paid plan order, used to show lower tiers as included in a hand-granted plan. */
const PAID_PLAN_RANK = { 'Pro': 1, 'Pro Plus': 2 };

function PricingPlans() {
  const { state: authState } = useAuth();
  const accountId = authState.activeAccount?.id;
  const currentPlanName = authState.currentMembership?.plan_name ?? "Free";

  const [loading, setLoading] = useState({});
  const [portalLoading, setPortalLoading] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Check for Stripe redirect parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get('billing');
    if (billing === 'success') {
      setSnackbar({
        open: true,
        message: 'Payment successful! Your plan has been activated.',
        severity: 'success',
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (billing === 'canceled') {
      setSnackbar({
        open: true,
        message: 'Checkout was canceled. You can try again anytime.',
        severity: 'info',
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Load whether this account already has a Stripe subscription (for portal button)
  useEffect(() => {
    if (!accountId) return;
    stripeService.getBillingStatus(accountId)
      .then((status) => setHasSubscription(status.has_subscription))
      .catch(() => {});
  }, [accountId]);

  /**
   * Start Stripe Checkout for upgrading from Free to a paid plan.
   * @param {string} planKey - PLAN_KEYS constant, e.g. 'pro-monthly'.
   */
  const handleCheckout = async (planKey) => {
    if (!accountId) {
      setSnackbar({ open: true, message: 'No active account found.', severity: 'error' });
      return;
    }
    setLoading((prev) => ({ ...prev, [planKey]: true }));
    try {
      await stripeService.startCheckout(accountId, planKey);
      // startCheckout redirects the browser; code below won't run on success
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to start checkout.', severity: 'error' });
    } finally {
      setLoading((prev) => ({ ...prev, [planKey]: false }));
    }
  };

  /**
   * Open the Stripe Customer Portal for managing an existing subscription.
   * Handles upgrades, downgrades, seat changes, and cancellations.
   */
  const handleManageBilling = async () => {
    if (!accountId) return;
    setPortalLoading(true);
    try {
      await stripeService.openBillingPortal(accountId);
      // openBillingPortal redirects the browser; code below won't run on success
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to open billing portal.', severity: 'error' });
    } finally {
      setPortalLoading(false);
    }
  };

  const handleContactSales = () => {
    window.open('mailto:sales@quillai.app?subject=Enterprise Plan Inquiry', '_blank');
  };

  const isCurrentPlan = (planName) =>
    currentPlanName.toLowerCase().replace(' plan', '') ===
    planName.toLowerCase().replace(' plan', '');

  const plans = [
    {
      name: "Free",
      price: 0,
      description: "No credit card required",
      features: [
        ...getPlanFeaturesForDisplay('free', { titleCase: true }),
        `${getModelDisplayName(MODEL_IDS.fast)} AI model`,
      ],
      planKey: null,
      color: "inherit",
      isPopular: false,
    },
    {
      name: "Pro",
      price: 29.99,
      description: "Perfect for individuals and small teams",
      features: [
        ...getPlanFeaturesForDisplay('pro', { titleCase: true }),
        `${getModelDisplayName(MODEL_IDS.premium)} AI model`,
        "Team collaboration",
      ],
      planKey: PLAN_KEYS.PRO_MONTHLY,
      color: "primary",
      isPopular: true,
    },
    {
      name: "Pro Plus",
      price: 49.99,
      description: "For power users and larger teams",
      features: [
        ...getPlanFeaturesForDisplay('pro-plus', { titleCase: true }),
        `${getModelDisplayName(MODEL_IDS.premium)} AI model`,
        "AI Notetaker",
        "AI Transcription",
        "Priority support",
      ],
      planKey: PLAN_KEYS.PRO_PLUS_MONTHLY,
      color: "primary",
      isPopular: false,
    },
    {
      name: "Enterprise",
      price: null,
      description: "Custom solutions for large organisations",
      features: [
        "Unlimited everything",
        `${getModelDisplayName(MODEL_IDS.premium)} AI model`,
        "AI Notetaker & Transcription",
        "Dedicated support",
        "Custom integrations",
        "SLA guarantee",
        "On-premise deployment",
      ],
      planKey: null,
      color: "inherit",
      isPopular: false,
    },
  ];

  const isFreeUser = isCurrentPlan('Free');

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Pick your plan
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Billed per team member. Adding or removing people updates your bill, prorated on the next invoice. Change or cancel any time from the billing portal.
        </Typography>
      </Box>

      <Grid container spacing={3} justifyContent="center">
        {plans.map((plan) => {
          const current = isCurrentPlan(plan.name);
          const isLoading = loading[plan.planKey];
          // A paid plan granted without a Stripe subscription already covers the lower paid
          // tiers; buying one would replace it, so those cards say "Included" instead.
          const included =
            !current && !hasSubscription && !isFreeUser && plan.planKey &&
            (PAID_PLAN_RANK[currentPlanName] ?? 0) >= (PAID_PLAN_RANK[plan.name] ?? 99);

          let buttonText = `Upgrade to ${plan.name}`;
          if (current) buttonText = 'Current Plan';
          else if (included) buttonText = 'Included';
          else if (plan.name === 'Free') buttonText = 'Downgrade to Free';
          else if (plan.name === 'Enterprise') buttonText = 'Contact Sales';

          const onClickAction = () => {
            if (current || included) return;
            if (plan.name === 'Enterprise') return handleContactSales();
            if (plan.name === 'Free') return; // downgrade handled in portal
            if (!isFreeUser && hasSubscription) return handleManageBilling();
            return handleCheckout(plan.planKey);
          };

          return (
            <Grid item xs={12} sm={6} md={3} key={plan.name}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  border: plan.isPopular ? "1.5px solid" : "1px solid",
                  borderColor: plan.isPopular ? "primary.main" : "divider",
                  boxShadow: plan.isPopular ? "0 0 0 3px var(--q-focus-halo)" : "none",
                  bgcolor: current ? "var(--q-bg-subtle)" : "background.paper",
                }}
              >
                {plan.isPopular && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -1,
                      left: "50%",
                      transform: "translateX(-50%)",
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      px: 1.5,
                      py: 0.25,
                      borderRadius: "0 0 8px 8px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    Most popular
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1, pt: plan.isPopular ? 4 : 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {plan.name}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h4" component="div" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {plan.price === null ? "Custom" : plan.price === 0 ? "Free" : `$${plan.price}`}
                      {plan.price !== null && plan.price > 0 && (
                        <Typography
                          component="span"
                          variant="body1"
                          color="text.secondary"
                          sx={{ fontSize: "1rem", fontWeight: 400 }}
                        >
                          /user/month
                        </Typography>
                      )}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {plan.description}
                  </Typography>

                  <List dense sx={{ mb: 3 }}>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle
                            sx={{
                              fontSize: 20,
                              color: current ? "text.secondary" : "success.main",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            variant: "body2",
                            color: current ? "text.secondary" : "text.primary",
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>

                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    variant={current ? "outlined" : "contained"}
                    color={plan.color}
                    fullWidth
                    size="large"
                    disabled={current || included || isLoading}
                    onClick={onClickAction}
                    sx={{ minHeight: 42 }}
                  >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : buttonText}
                  </Button>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Manage Billing — shown whenever the account has an active Stripe subscription */}
      {hasSubscription && (
        <Box
          sx={{
            mt: 4,
            p: 3,
            bgcolor: "background.paper",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CreditCard fontSize="small" />
              Manage your subscription
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Change your plan, update your payment method, see invoices, or cancel, all in one place.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={portalLoading ? <CircularProgress size={16} /> : <CreditCard />}
            disabled={portalLoading}
            onClick={handleManageBilling}
          >
            Open Billing Portal
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/**
 * Fathom AI integration card with API key management and auto-sync toggle.
 * Manages its own state independently from general settings.
 * @returns {JSX.Element} Fathom integration panel.
 */
function FathomIntegration() {
  const { state: authState } = useAuth();
  const [fathomApiKey, setFathomApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [fathomAutoSync, setFathomAutoSync] = useState(false);
  const [fathomWebhookId, setFathomWebhookId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [togglingSync, setTogglingSync] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [hasKeyChanged, setHasKeyChanged] = useState(false);

  /**
   * Load integration data from Supabase on mount.
   */
  useEffect(() => {
    const loadIntegration = async () => {
      if (!authState.user || !authState.activeAccount) {
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from("integrations")
          .select("*")
          .eq("user_id", authState.user.id)
          .eq("account_id", authState.activeAccount.id)
          .single();

        if (fetchError && fetchError.code !== "PGRST116") {
          throw fetchError;
        }

        if (data) {
          setFathomApiKey(data.fathom_api_key || "");
          setFathomAutoSync(data.fathom_auto_sync || false);
          setFathomWebhookId(data.fathom_webhook_id || null);
          setVerified(!!data.fathom_api_key);
        }
      } catch (err) {
        console.error("Error loading integration:", err);
        setError("Failed to load integration settings.");
      } finally {
        setLoading(false);
      }
    };

    loadIntegration();
  }, [authState.user, authState.activeAccount]);

  /**
   * Save and verify the Fathom API key.
   * Persists to integrations table and tests validity via Edge Function.
   */
  const handleSaveAndVerify = async () => {
    if (!fathomApiKey.trim()) {
      setError("Please enter an API key.");
      return;
    }

    setError("");
    setSuccess("");
    setSaving(true);
    setVerifying(true);

    try {
      // First, test the key via Edge Function
      const testResult = await edgeFunctionService.callFunction("fathom-api", {
        action: "test-key",
        apiKey: fathomApiKey.trim(),
      });

      if (!testResult.success) {
        throw new Error(testResult.error || "API key verification failed.");
      }

      // Key is valid -- save to integrations table
      const { error: upsertError } = await supabase
        .from("integrations")
        .upsert(
          {
            user_id: authState.user.id,
            account_id: authState.activeAccount.id,
            fathom_api_key: fathomApiKey.trim(),
          },
          { onConflict: "user_id,account_id" }
        );

      if (upsertError) throw upsertError;

      setVerified(true);
      setHasKeyChanged(false);
      setSuccess("API key verified and saved successfully.");
    } catch (err) {
      console.error("Error saving/verifying Fathom API key:", err);
      setVerified(false);
      setError(err.message || "Failed to verify API key.");
    } finally {
      setSaving(false);
      setVerifying(false);
    }
  };

  /**
   * Toggle auto-sync on or off.
   * Creates or deletes the Fathom webhook accordingly.
   * @param {boolean} enabled - Whether auto-sync should be enabled.
   */
  const handleToggleAutoSync = async (enabled) => {
    setError("");
    setSuccess("");
    setTogglingSync(true);

    try {
      if (enabled) {
        // Create webhook via Edge Function
        const result = await edgeFunctionService.callFunction("fathom-api", {
          action: "create-webhook",
          userId: authState.user.id,
          accountId: authState.activeAccount.id,
        });

        if (!result.success) {
          throw new Error(result.error || "Failed to create webhook.");
        }

        setFathomWebhookId(result.webhookId);
        setFathomAutoSync(true);
        setSuccess("Auto-sync enabled. New meetings will be synced automatically.");
      } else {
        // Delete webhook via Edge Function
        const result = await edgeFunctionService.callFunction("fathom-api", {
          action: "delete-webhook",
          userId: authState.user.id,
          accountId: authState.activeAccount.id,
        });

        if (!result.success) {
          throw new Error(result.error || "Failed to delete webhook.");
        }

        setFathomWebhookId(null);
        setFathomAutoSync(false);
        setSuccess("Auto-sync disabled. Webhook has been removed.");
      }
    } catch (err) {
      console.error("Error toggling auto-sync:", err);
      setError(err.message || "Failed to toggle auto-sync.");
    } finally {
      setTogglingSync(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }} aria-busy="true" aria-label="Loading integrations">
        <Skeleton variant="text" width={180} height={32} />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="rounded" height={180} sx={{ mt: 2, borderRadius: "var(--q-radius-xl)" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
      >
        <Sync />
        Integrations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect external services to automatically sync meeting data and
        recordings.
      </Typography>

      {/* Fathom Integration Card */}
      <Card
        variant="outlined"
        sx={{
          borderColor: "divider",
          borderRadius: "var(--q-radius-xl)",
        }}
      >
        <CardContent>
          {/* Header with logo and status */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
              <Box
                component="img"
                src="/integration_logo/fathom-video-icon.png"
                alt="Fathom AI"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  objectFit: "contain",
                }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Fathom AI
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sync meeting recordings, transcripts, and summaries
                </Typography>
              </Box>
            </Box>
            <Chip
              icon={verified ? <CheckCircle /> : <LinkOff />}
              label={verified ? "Connected" : "Not connected"}
              color={verified ? "success" : "default"}
              size="small"
            />
          </Box>

          {/* API Key Input */}
          <TextField
            fullWidth
            label="Fathom API Key"
            type={showApiKey ? "text" : "password"}
            value={fathomApiKey}
            onChange={(e) => {
              setFathomApiKey(e.target.value);
              setHasKeyChanged(true);
              setVerified(false);
            }}
            placeholder="Enter your Fathom API key"
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowApiKey(!showApiKey)}
                    edge="end"
                    size="small"
                    aria-label={showApiKey ? "Hide API key" : "Show API key"}
                  >
                    {showApiKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="You can find your API key in your Fathom account settings."
          />

          {/* Save & Verify Button */}
          <Button
            variant="contained"
            onClick={handleSaveAndVerify}
            disabled={saving || !fathomApiKey.trim() || (!hasKeyChanged && verified)}
            startIcon={
              verifying ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CheckCircle />
              )
            }
            sx={{ mb: 2 }}
          >
            {verifying
              ? "Verifying..."
              : verified && !hasKeyChanged
              ? "Verified"
              : "Save & verify"}
          </Button>

          {/* Auto-Sync Toggle -- only visible when key is verified */}
          {verified && !hasKeyChanged && (
            <Box
              sx={{
                mt: 1,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" component="label" htmlFor="fathom-auto-sync">
                    Auto-sync meetings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Automatically receive meeting transcripts and summaries when
                    recordings are ready.
                  </Typography>
                </Box>
                <Switch
                  checked={fathomAutoSync}
                  onChange={(e) => handleToggleAutoSync(e.target.checked)}
                  disabled={togglingSync}
                  inputProps={{ id: "fathom-auto-sync" }}
                />
              </Box>
              {togglingSync && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    {fathomAutoSync
                      ? "Removing webhook..."
                      : "Setting up webhook..."}
                  </Typography>
                </Box>
              )}
              {fathomAutoSync && fathomWebhookId && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Webhook active (ID: {fathomWebhookId.substring(0, 8)}...)
                </Typography>
              )}
            </Box>
          )}

          {/* Status Messages */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} icon={<ErrorOutline />}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}


/** Settings tab index of the Email section. */
export const SETTINGS_TAB_EMAIL = 3;

/** Shown when turning product emails back on is refused (the address has a spam complaint or a manual block). */
export const EMAIL_RESUBSCRIBE_BLOCKED =
  "We can't turn product emails back on for this address. Contact info@quillai.app.";

/**
 * Email preferences: one switch for product update emails, saved as soon as it changes
 * through the email-unsubscribe function (get_preference / set_preference).
 * @returns {JSX.Element} Email section.
 */
export function EmailPreferencesSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscribed, setSubscribed] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [blockedMessage, setBlockedMessage] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  // Re-entry guard: the switch stays enabled (and focused) while a save runs.
  const savingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    getMarketingPreference()
      .then((preference) => {
        if (!cancelled) setSubscribed(!preference.marketingOptOut);
      })
      .catch(() => {
        if (!cancelled) setLoadError("We couldn't load your email preferences.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const handleToggle = async (event) => {
    if (savingRef.current) return;
    savingRef.current = true;
    const next = event.target.checked;
    const previous = subscribed;
    setSubscribed(next);
    setSaving(true);
    setSaveError("");
    setSavedMessage("");
    setBlockedMessage("");
    try {
      const saved = await setMarketingPreference(!next);
      setSubscribed(!saved.marketingOptOut);
      if (next && saved.marketingOptOut) {
        // Asked to subscribe, but the server keeps the address opted out (complaint or manual block).
        setBlockedMessage(EMAIL_RESUBSCRIBE_BLOCKED);
      } else {
        setSavedMessage(saved.marketingOptOut ? "Product emails turned off." : "Product emails turned on.");
      }
    } catch {
      setSubscribed(previous);
      setSaveError("We couldn't save that change. Please try again.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography
        variant="h6"
        component="h2"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <MailOutline fontSize="small" />
        Email
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose which emails Quill AI sends you.
      </Typography>

      {loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((key) => key + 1)}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: "var(--q-radius-xl)" }}>
          <CardContent>
            {loading ? (
              <Skeleton variant="rounded" height={40} aria-label="Loading email preferences" />
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={subscribed}
                    onChange={handleToggle}
                    inputProps={{
                      "aria-describedby": "settings-email-marketing-help",
                      "aria-busy": saving ? "true" : undefined,
                    }}
                  />
                }
                label="Product updates and tips by email"
              />
            )}
            <Typography
              id="settings-email-marketing-help"
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              Occasional emails about new features in Quill AI. Invites, password resets and
              billing emails always arrive.
            </Typography>
            <Box aria-live="polite" sx={{ mt: saveError || savedMessage || blockedMessage ? 1.5 : 0 }}>
              {saveError && <Alert severity="error">{saveError}</Alert>}
              {!saveError && blockedMessage && <Alert severity="warning">{blockedMessage}</Alert>}
              {!saveError && savedMessage && (
                <Typography variant="body2" color="text.secondary">
                  {savedMessage}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

/** Zero-based tab index for the Pricing tab (AI Settings = 0, Pricing = 1, Integrations = 2). */
export const SETTINGS_TAB_PRICING = 1;

/** Settings tab index of the Autopilot section (shown when any of the user's workspaces has Autopilot). */
export const SETTINGS_TAB_AUTOPILOT = 4;

/** Highest tab index the Settings dialog can open on. */
export const SETTINGS_TAB_MAX = SETTINGS_TAB_AUTOPILOT;

/** `/settings?tab=<name>` names (email links use these). */
export const SETTINGS_TAB_BY_NAME = Object.freeze({
  ai: 0,
  pricing: SETTINGS_TAB_PRICING,
  integrations: 2,
  email: SETTINGS_TAB_EMAIL,
  autopilot: SETTINGS_TAB_AUTOPILOT,
});

/**
 * Settings modal for AI and billing configuration.
 * @param {object} props - Component props.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {() => void} props.onClose - Close handler.
 * @param {number} [props.initialTab=0] - Tab to show when the dialog opens (0 = AI, 1 = Pricing, 2 = Integrations, 3 = Email, 4 = Autopilot).
 * @returns {JSX.Element} Settings dialog.
 */
function SettingsDialog({ open, onClose, initialTab = 0 }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const { state: authState } = useAuth();
  const { state, actions } = useSettings();
  const [activeTab, setActiveTab] = useState(0);
  const [localSettings, setLocalSettings] = useState({
    openaiApiKey: "",
    anthropicApiKey: "",
    googleApiKey: "",
    defaultModel: MODEL_IDS.fast,
    theme: "light",
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  // Autopilot settings are per user, so the tab shows when any of the user's workspaces has it.
  const showAutopilot =
    isAutopilotEnabled(authState.activeAccount) || (authState.accounts || []).some(isAutopilotEnabled);

  // Sync visible tab when opening (e.g. open directly to Pricing from Home)
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab === SETTINGS_TAB_AUTOPILOT && !showAutopilot ? 0 : initialTab);
    }
  }, [open, initialTab, showAutopilot]);

  // Reset local settings when dialog opens
  useEffect(() => {
    if (open) {
      const availableModels = actions.getAvailableModels();
      let defaultModel = state.planModelId || state.defaultModel || MODEL_IDS.fast;

      // If the current default model isn't available, choose the first available one
      if (availableModels.length > 0) {
        const isModelAvailable = availableModels.some(
          (model) => model.id === defaultModel
        );
        if (!isModelAvailable) {
          console.warn(
            `Model ${defaultModel} not available, switching to ${availableModels[0].id}`
          );
          defaultModel = availableModels[0].id;
        }
      }

      setLocalSettings({
        openaiApiKey: state.openaiApiKey || "",
        anthropicApiKey: state.anthropicApiKey || "",
        googleApiKey: state.googleApiKey || "",
        defaultModel: defaultModel,
        theme: state.theme || "light",
      });
      setHasUnsavedChanges(false);
    }
  }, [open, state, actions]);

  // Update available models when API keys change
  useEffect(() => {
    if (open) {
      const availableModels = actions.getAvailableModels(localSettings);

      // If the current default model is no longer available, switch to the first available one
      if (availableModels.length > 0) {
        const isModelAvailable = availableModels.some(
          (model) => model.id === localSettings.defaultModel
        );
        if (!isModelAvailable) {
          console.warn(
            `Model ${localSettings.defaultModel} no longer available, switching to ${availableModels[0].id}`
          );
          setLocalSettings((prev) => ({
            ...prev,
            defaultModel: availableModels[0].id,
          }));
          setHasUnsavedChanges(true);
        }
      }
    }
  }, [
    open,
    localSettings.openaiApiKey,
    localSettings.anthropicApiKey,
    localSettings.googleApiKey,
    localSettings.defaultModel,
    localSettings,
    actions,
  ]);

  const handleSettingChange = (key, value) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await actions.updateSettings(localSettings);
      if (result && result.success) {
        setHasUnsavedChanges(false);
      } else {
        throw new Error(result?.error || "Unknown error occurred");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      // The error will be shown through the context state
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      // Reset local settings to saved values
      setLocalSettings({
        openaiApiKey: state.openaiApiKey || "",
        anthropicApiKey: state.anthropicApiKey || "",
        googleApiKey: state.googleApiKey || "",
        defaultModel: state.planModelId || state.defaultModel || MODEL_IDS.fast,
        theme: state.theme || "light",
      });
      setHasUnsavedChanges(false);
    }
    onClose();
  };

  // Same tier the server uses to pick the model (plan_name only).
  const aiTier = getAiTier(authState.currentMembership);
  const isPremiumAiTier = isPremiumTier(aiTier);
  const planModelName = getModelDisplayName(getPlanModelId(aiTier));
  const fastModelName = getModelDisplayName(MODEL_IDS.fast);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isPhone}
      disableRestoreFocus
      aria-labelledby="settings-dialog-title"
      sx={{
        "& .MuiDialog-paper": isPhone
          ? {}
          : {
              height: "90vh",
              maxHeight: "90vh",
            },
      }}
    >
      <DialogTitle id="settings-dialog-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box component="span" sx={{ flex: 1 }}>Settings</Box>
        <IconButton onClick={handleClose} size="small" aria-label="Close settings">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Settings sections"
          sx={{
            px: { xs: 1, sm: 2 },
            borderBottom: 1,
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "background.paper",
          }}
        >
          <Tab label="AI" />
          <Tab label="Pricing" />
          <Tab label="Integrations" />
          <Tab label="Email" />
          {showAutopilot && <Tab label="Autopilot" />}
          {/*<Tab label="Appearance" />*/}
        </Tabs>

        {/* API Keys Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <AutoAwesome fontSize="small" />
              AI configuration
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your AI is ready for document processing and analysis. AI access is
              managed securely on the server.
            </Typography>

            <Card variant="outlined" sx={{ mb: 3, borderRadius: "var(--q-radius-xl)" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    AI capability for your plan
                  </Typography>
                  <Chip
                    label={planModelName}
                    icon={isPremiumAiTier ? <AutoAwesome /> : undefined}
                    color={isPremiumAiTier ? "primary" : "default"}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  {isPremiumAiTier
                    ? `Your plan runs Inky chat, document generation, requirement extraction and template analysis on ${planModelName}. Inline editor actions and quick document edits use ${fastModelName} for speed, and move up to ${planModelName} automatically when an edit needs it. Meeting summaries and action items use ${getModelDisplayName(MODEL_IDS.lite)} on every plan.`
                    : `Your plan runs AI features on ${fastModelName}, and meeting summaries and action items on ${getModelDisplayName(MODEL_IDS.lite)}. Upgrade to Pro, Pro Plus or Enterprise to run Inky chat, document generation and extraction on ${getModelDisplayName(MODEL_IDS.premium)}.`}
                </Typography>
              </CardContent>
            </Card>

            {/* Advanced Settings - Keep API key structure hidden but available */}
            <Box sx={{ display: 'none' }}>
              {/* Hidden API key inputs - keeping structure for future Option B pivot */}
              <TextField
                value={localSettings.openaiApiKey}
                onChange={(e) => handleSettingChange("openaiApiKey", e.target.value)}
              />
              <TextField
                value={localSettings.anthropicApiKey}
                onChange={(e) => handleSettingChange("anthropicApiKey", e.target.value)}
              />
              <TextField
                value={localSettings.googleApiKey}
                onChange={(e) => handleSettingChange("googleApiKey", e.target.value)}
              />
            </Box>

            {hasUnsavedChanges && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Settings changes need to be saved before they take effect.
              </Alert>
            )}
          </Box>
        </TabPanel>

        {/* Pricing Tab */}
        <TabPanel value={activeTab} index={1}>
          <PricingPlans />
        </TabPanel>

        {/* Integrations Tab */}
        <TabPanel value={activeTab} index={2}>
          <FathomIntegration />
        </TabPanel>

        {/* Email Tab */}
        <TabPanel value={activeTab} index={SETTINGS_TAB_EMAIL}>
          <EmailPreferencesSection />
        </TabPanel>

        {/* Autopilot Tab */}
        {showAutopilot && (
          <TabPanel value={activeTab} index={SETTINGS_TAB_AUTOPILOT}>
            <AutopilotSettingsSection />
          </TabPanel>
        )}

        {/* Appearance Tab (no tab shows it today) */}
        <TabPanel value={activeTab} index={99}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Appearance
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Customize the appearance of your Quill AI workspace.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={localSettings.theme === "dark"}
                  onChange={(e) =>
                    handleSettingChange(
                      "theme",
                      e.target.checked ? "dark" : "light"
                    )
                  }
                />
              }
              label="Dark Mode"
            />

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Switch between light and dark themes for better readability.
            </Typography>
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button
          component="a"
          {...WHATS_NEW_LINK_PROPS}
          startIcon={<NewReleasesOutlined fontSize="small" />}
          aria-label="What's new (opens in a new tab)"
          sx={{ mr: "auto", textTransform: "none" }}
        >
          What&apos;s new
        </Button>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasUnsavedChanges || saving}
          startIcon={saving ? <Save /> : <Save />}
        >
          {saving ? "Saving..." : "Save settings"}
        </Button>
      </DialogActions>

      {state.error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {state.error}
        </Alert>
      )}
    </Dialog>
  );
}

export default SettingsDialog;
